<template>
    <div class="dbresult">
        <!-- 错误 -->
        <transition name="dbresult-anim" @enter="onPanelEnter" @after-enter="onPanelAfterEnter"
            @leave="onPanelLeave" @after-leave="onPanelAfterLeave">
            <div v-if="error" key="err" class="dbresult__error">{{ error }}</div>
        </transition>

        <!-- 结果体：执行中【不卸载旧结果】，只有「表 ↔ 非表 ↔ 错误」这种形变才做一次过渡 -->
        <transition name="dbresult-anim" @enter="onPanelEnter" @after-enter="onPanelAfterEnter"
            @leave="onPanelLeave" @after-leave="onPanelAfterLeave">
            <div v-if="!error && result" key="res" class="dbresult__body">
                <div class="dbresult__status">
                    <template v-if="result.type === 'QUERY'">返回 {{ result.rowCount }} 行</template>
                    <template v-else>执行成功，影响 {{ result.affectedRows }} 行</template>
                    <span class="dbresult__sep">·</span>
                    <span>耗时 {{ result.elapsedMs }} ms</span>
                    <template v-if="sql">
                        <span class="dbresult__sep">·</span>
                        <!-- 执行的 SQL：超长省略，悬浮 title 查看完整语句 -->
                        <span class="dbresult__sql" :title="sql">{{ sql }}</span>
                    </template>
                </div>

                <el-alert v-for="(w, i) in (result.warnings || [])" :key="i" :title="w" type="warning"
                    :closable="false" show-icon class="dbresult__warn" />

                <!-- 表格区：表↔非表时收起/展开；表↔表时数据就地替换、不做动画 -->
                <transition name="dbresult-anim" @enter="onPanelEnter" @after-enter="onPanelAfterEnter"
                    @leave="onPanelLeave" @after-leave="onPanelAfterLeave">
                    <div v-if="showTable" key="tbl">
                        <div class="dbresult__actions">
                            <el-button size="small" type="primary"
                                :disabled="selection.length === 0 || selection.length > maxSelect"
                                @click="emitFavorite">
                                收藏所选行（{{ selection.length }}）
                            </el-button>
                            <span class="dbresult__tips">选中 1~100 行可收藏；需先保存该 SQL 块</span>
                        </div>
                        <div class="dbresult__toolbar">
                            <el-input v-model="keyword" class="dbresult__search" size="small" clearable
                                placeholder="搜索结果（关键字匹配所有列）">
                                <template #prefix>
                                    <el-icon>
                                        <Search />
                                    </el-icon>
                                </template>
                            </el-input>
                            <el-button size="small" @click="openColumnSetting">
                                <el-icon class="el-icon--left">
                                    <Setting />
                                </el-icon>
                                列设置
                            </el-button>
                            <span v-if="keyword" class="dbresult__match">匹配 {{ matchedRows }} / {{ totalRows }} 行</span>
                            <span class="dbresult__tips">{{ canEdit ? '双击单元格进入编辑；点击表头可排序；拖动表头边界可调整列宽' : '点击表头可排序；拖动表头边界可调整列宽；要固定某列请点「列设置」' }}</span>
                            <!-- 结果可编辑（简单单表查询 + 写权限）：右上角行级编辑操作 -->
                            <span v-if="canEdit" class="dbresult__editbar">
                                <el-button size="small" @click="addRow">新增行</el-button>
                                <el-button size="small" :disabled="selection.length === 0" @click="deleteSelectedRows">
                                    删除行
                                </el-button>
                                <el-button size="small" type="primary" :disabled="!canSubmit" :loading="savingEdit"
                                    @click="submitChanges">提交</el-button>
                                <el-button size="small" :disabled="!canSubmit" @click="rollbackChanges">回滚</el-button>
                                <span v-if="pendingCount" class="dbresult__editcount">{{ pendingCount }} 处待提交</span>
                            </span>
                            <el-tooltip v-else-if="editReason" :content="editReason" placement="top">
                                <span class="dbresult__editlock">暂不可编辑</span>
                            </el-tooltip>
                        </div>
                        <!-- vxe-table：行数超过 20 自动启用虚拟滚动；排序/固定列均由用户在「列设置」里决定 -->
                        <vxe-table ref="xTable" class="dbresult__table" :data="viewData" :max-height="300" size="small"
                            border stripe show-overflow :row-config="{ keyField: '__idx', isHover: true }"
                            :column-config="{ resizable: true }"
                            :edit-config="editConfig"
                            :row-class-name="rowClassName"
                            :scroll-y="{ enabled: true, gt: 20 }"
                            :sort-config="{ trigger: 'cell', orders: ['asc', 'desc', null] }"
                            @edit-actived="onEditActived" @edit-closed="onEditClosed"
                            @checkbox-change="onCheckboxChange" @checkbox-all="onCheckboxChange">
                            <vxe-column type="checkbox" width="44" />
                            <vxe-column type="seq" width="52" title="#" />
                            <vxe-column v-for="(col, ci) in result.columns" :key="ci" :field="'c' + ci"
                                :title="col.name" :min-width="colWidth(col)" :sortable="true"
                                :edit-render="columnEditRender(col)">
                                <!-- 表头三行：字段名 / 字段类型 / 字段注释（注释按来源表字段匹配，见 colComment） -->
                                <template #header>
                                    <div class="dbresult__head">
                                        <span class="dbresult__head-name">{{ col.name }}</span>
                                        <span v-if="colType(col)" class="dbresult__head-type">{{ colType(col) }}</span>
                                        <span v-if="colComment(col)" class="dbresult__head-cmt"
                                            :title="colComment(col)">{{ colComment(col) }}</span>
                                    </div>
                                </template>
                                <template #default="{ row }">
                                    <!-- 默认纯文本（双击由 vxe 进入编辑，见 editConfig）；改动高亮 -->
                                    <span v-if="isCellEditable(row, ci)" class="dbresult__cell-view"
                                        :class="{ 'is-dirty': isCellDirty(row, ci) }" title="双击编辑">{{
                                        row['c' + ci] === null ? '' : row['c' + ci] }}</span>
                                    <span v-else-if="row['c' + ci] === null" class="dbresult__null">NULL</span>
                                    <span v-else>{{ row['c' + ci] }}</span>
                                </template>
                            </vxe-column>
                        </vxe-table>
                    </div>
                </transition>
            </div>
        </transition>

        <!-- 执行中：保留旧结果，在其上叠加「转圈等待」；首次执行无旧结果，只走正常展开动画 -->
        <transition name="dbresult-fade">
            <div v-if="loading && result && !error" class="dbresult__loading">
                <el-icon class="is-loading" :size="22">
                    <Loading />
                </el-icon>
            </div>
        </transition>
    </div>
</template>

<script>
/**
 * 结果面板
 * <p>拆成独立子组件的目的：让「大结果表格」只在结果变化时重渲染，
 * 避免父级（如 SQL 输入框打字）的每次重渲染都连带重算表格。</p>
 */
import { ElMessage } from 'element-plus';

export default {
    name: 'DbResultPanel',
    props: {
        // 结果对象（父级已 markRaw，避免深层响应式）
        result: { type: Object, default: null },
        error: { type: String, default: '' },
        // 是否正在执行：为 true 时保留旧结果并在其上叠加转圈遮罩
        loading: { type: Boolean, default: false },
        // 本次执行的 SQL（展示在状态行末尾，超长省略、悬浮查看完整）
        sql: { type: String, default: '' },
        // 列元数据：`{ 表名: [{ name, type, comment }] }`（父组件传当前库的列缓存）。
        // 结合结果列自带的 `table`/`column`（后端 JDBC 元数据，已解析别名）匹配字段注释。
        columnMeta: { type: Object, default: null },
        // 当前连接是否可写（owner / 读写团队）——与后端返回的 result.edit 共同决定能否编辑
        canWrite: { type: Boolean, default: false },
        /**
         * 提交改动：由父组件注入（负责生产防护锁确认 + 调接口 + 成功后重查刷新）。
         * 入参 `{ table, schema, changes }`，返回 Promise<boolean>（true=提交成功）。
         */
        commitHandler: { type: Function, default: null },
        maxSelect: { type: Number, default: 100 }
    },
    emits: ['favorite'],
    data() {
        return {
            selection: [],
            keyword: '',
            // 编辑态：结果副本（可改），仅 canEdit 时作为表格数据源
            editRows: [],
            // 待删除的原有行（提交时生成 DELETE）
            deletedRows: [],
            // 是否正处于单元格编辑中（双击出现输入框即置真）→ 提交/回滚按钮点亮
            editActive: false,
            // 提交中
            savingEdit: false,
            // 新增行/键的递增序号（保证 vxe keyField 唯一）
            editSeq: 0
        };
    },
    computed: {
        // 是否展示表格区：仅「查询且有列」时
        showTable() {
            return !!(this.result && this.result.type === 'QUERY' && (this.result.columns || []).length);
        },
        // 后端返回的可编辑元信息
        editMeta() {
            return (this.result && this.result.edit) || null;
        },
        // 是否进入可编辑：写权限 + 后端判定为可编辑 + 有表格
        canEdit() {
            return !!(this.canWrite && this.showTable && this.editMeta && this.editMeta.editable);
        },
        // 不可编辑的原因（用于悬浮提示）
        editReason() {
            return this.editMeta && !this.editMeta.editable ? (this.editMeta.reason || '') : '';
        },
        // 结果原始行（只读副本）；仅在 result 引用变化时重算（result 为 markRaw）
        originTableData() {
            if (!this.result || !this.result.columns) return [];
            return (this.result.rows || []).map((row, ri) => {
                const obj = { __idx: ri };
                row.forEach((cell, ci) => {
                    obj['c' + ci] = cell;
                });
                return obj;
            });
        },
        // 表格数据源：可编辑时用编辑副本，否则用原始行
        tableData() {
            return this.canEdit ? this.editRows : this.originTableData;
        },
        totalRows() {
            return this.tableData.length;
        },
        // 关键字跨所有列过滤（不区分大小写）
        viewData() {
            const key = this.keyword.trim().toLowerCase();
            if (!key) return this.tableData;
            const columns = this.result.columns || [];
            return this.tableData.filter(row => {
                for (let ci = 0; ci < columns.length; ci++) {
                    const cell = row['c' + ci];
                    if (cell !== null && cell !== undefined && String(cell).toLowerCase().includes(key)) {
                        return true;
                    }
                }
                return false;
            });
        },
        matchedRows() {
            return this.viewData.length;
        },
        // 可编辑列（原始列名，小写集合）
        editableColumnSet() {
            const list = (this.editMeta && this.editMeta.editableColumns) || [];
            return new Set(list.map(c => String(c).toLowerCase()));
        },
        // 主键列（原始列名，小写集合）
        pkColumnSet() {
            const list = (this.editMeta && this.editMeta.pkColumns) || [];
            return new Set(list.map(c => String(c).toLowerCase()));
        },
        // 待提交改动数量（新增 + 修改 + 删除）
        pendingCount() {
            if (!this.canEdit) return 0;
            return this.collectChanges().length;
        },
        // 提交/回滚是否可用：有改动，或正处于单元格编辑中（双击出现输入框即点亮）
        canSubmit() {
            return this.editActive || this.pendingCount > 0;
        },
        // vxe 单元格编辑配置（对象无响应式依赖 → 只创建一次，props 引用稳定）
        editConfig() {
            return {
                trigger: 'dblclick',   // 双击进入编辑（避免误操作）
                mode: 'cell',
                showIcon: false,
                // 返回 false 时该单元格不可编辑（只读列 / 原有行的主键列）
                beforeEditMethod: ({ row, column }) => this.canEditCellField(row, column.field)
            };
        }
    },
    watch: {
        result() {
            // 结果整体替换，清掉搜索词与选中状态，并重建编辑副本
            this.keyword = '';
            this.clearSelection();
            this.resetEditRows();
        },
        keyword() {
            // 过滤后可能隐藏已选行，清空选中避免「看不到却被收藏」
            this.clearSelection();
        }
    },
    mounted() {
        // 首次挂载即初始化编辑副本（后续 result 变化由 watcher 处理）
        this.resetEditRows();
    },
    methods: {
        clearSelection() {
            this.selection = [];
            // 结果/筛选变化后，清掉 vxe-table 内部的行选中状态（keyField 相同可能残留误选）
            this.$nextTick(() => {
                const table = this.$refs.xTable;
                if (table && table.clearCheckboxRow) {
                    table.clearCheckboxRow();
                }
            });
        },
        // vxe-table 的 checkbox-change / checkbox-all 事件都会回传 records
        onCheckboxChange({ records }) {
            this.selection = records || [];
        },
        // 打开 vxe 自带的列设置面板：显隐、拖拽排序、左/右固定，全部由用户自己点
        openColumnSetting() {
            const table = this.$refs.xTable;
            if (table && table.openCustom) {
                table.openCustom();
            }
        },
        emitFavorite() {
            if (this.selection.length === 0 || this.selection.length > this.maxSelect) return;
            const columns = this.result.columns || [];
            const rows = this.selection.map(item => columns.map((col, ci) => item['c' + ci]));
            this.$emit('favorite', rows);
        },
        // ---------- 结果表格编辑（简单单表查询，见 [11 篇]） ----------
        // 重建编辑副本（进入编辑 / 回滚 / 结果刷新时调用）
        resetEditRows() {
            this.deletedRows = [];
            this.selection = [];
            this.editActive = false;
            this.editSeq = 0;
            if (!this.canEdit) {
                this.editRows = [];
                return;
            }
            const columns = this.result.columns || [];
            this.editRows = (this.result.rows || []).map((row) => {
                const obj = { __idx: this.editSeq++, __new: false };
                const orig = [];
                columns.forEach((col, ci) => {
                    const v = row[ci] === undefined ? null : row[ci];
                    obj['c' + ci] = v;
                    orig.push(v);
                });
                obj.__orig = orig;
                return obj;
            });
        },
        // 单元格值归一化（用于比较是否改动）
        normCell(v) {
            return v === null || v === undefined ? '' : String(v);
        },
        // 原始列名在结果列中的下标（按 col.column 匹配）
        columnIndexOf(columnName) {
            const columns = this.result.columns || [];
            const want = String(columnName).toLowerCase();
            for (let ci = 0; ci < columns.length; ci++) {
                const c = columns[ci];
                if (c.column && String(c.column).toLowerCase() === want) return ci;
            }
            return -1;
        },
        // 该行对应的主键键值对象（{ 原始列名: 值 }）
        rowKeys(row) {
            const keys = {};
            (this.editMeta && this.editMeta.pkColumns ? this.editMeta.pkColumns : []).forEach(pk => {
                const ci = this.columnIndexOf(pk);
                if (ci >= 0) keys[pk] = row['c' + ci];
            });
            return keys;
        },
        // 该列是否需要编辑能力（可编辑列，或主键列——主键列仅「新增行」可填，
        // 是否允许由 beforeEditMethod 按行判定；列级先开启 edit-render）
        columnEditRender(col) {
            if (!this.canEdit || !col || !col.column) return null;
            const name = String(col.column).toLowerCase();
            if (this.editableColumnSet.has(name) || this.pkColumnSet.has(name)) {
                // immediate：输入即时写回行数据（否则值只留在编辑器 model 里，提交时取不到）
                return { name: 'input', immediate: true };
            }
            return null;
        },
        // 单元格是否可编辑：可编辑列可编辑；新增行的主键列也可填写
        isCellEditable(row, ci) {
            if (!this.canEdit) return false;
            const col = (this.result.columns || [])[ci];
            if (!col || !col.column) return false;
            const name = String(col.column).toLowerCase();
            if (this.editableColumnSet.has(name)) return true;
            return !!(row && row.__new && this.pkColumnSet.has(name));
        },
        // vxe beforeEditMethod：按列 field（'c{索引}'）判断该单元格能否编辑
        canEditCellField(row, field) {
            if (!this.canEdit || !field || field.charAt(0) !== 'c') return false;
            const ci = Number(field.slice(1));
            return Number.isInteger(ci) && this.isCellEditable(row, ci);
        },
        // 单元格进入编辑（双击出现输入框）→ 提交/回滚点亮
        onEditActived() {
            this.editActive = true;
        },
        // 单元格退出编辑
        onEditClosed() {
            this.editActive = false;
        },
        // 单元格是否已被修改（用于高亮）
        isCellDirty(row, ci) {
            if (!row || row.__new) return false;
            const col = (this.result.columns || [])[ci];
            if (!col || !col.column) return false;
            if (!this.editableColumnSet.has(String(col.column).toLowerCase())) return false;
            return this.normCell(row['c' + ci]) !== this.normCell(row.__orig ? row.__orig[ci] : null);
        },
        // 新增行整行高亮
        rowClassName({ row }) {
            return row && row.__new ? 'dbresult__row--new' : '';
        },
        // 新增一行（空白行，提交时生成 INSERT）
        addRow() {
            if (!this.canEdit) return;
            const columns = this.result.columns || [];
            const row = { __idx: this.editSeq++, __new: true, __orig: null };
            columns.forEach((col, ci) => {
                row['c' + ci] = '';
            });
            // 第一个可编辑列（用于自动进入编辑）
            let firstCi = -1;
            for (let ci = 0; ci < columns.length; ci++) {
                if (this.isCellEditable(row, ci)) {
                    firstCi = ci;
                    break;
                }
            }
            // 赋新数组（而非 push）：vxe-table 依据 data 引用变化触发更新
            this.editRows = this.editRows.concat(row);
            this.$nextTick(() => {
                const table = this.$refs.xTable;
                const target = this.editRows[this.editRows.length - 1];
                if (table && table.scrollToRow && target) table.scrollToRow(target);
                // 自动进入第一个可编辑单元格
                if (table && table.setEditCell && target && firstCi >= 0) {
                    table.setEditCell(target, 'c' + firstCi);
                }
            });
        },
        // 删除勾选的行（未提交前只标记，可回滚）
        deleteSelectedRows() {
            if (!this.selection.length) {
                ElMessage({ message: '请先勾选要删除的行', type: 'warning', duration: 2000 });
                return;
            }
            const selected = this.selection.slice();
            const keep = [];
            this.editRows.forEach(row => {
                if (selected.indexOf(row) >= 0) {
                    // 原有行 → 记录为待删除（生成 DELETE）；新增行 → 直接丢弃
                    if (!row.__new) this.deletedRows.push(row);
                } else {
                    keep.push(row);
                }
            });
            this.editRows = keep;
            this.clearSelection();
        },
        // 回滚：丢弃全部本地改动
        rollbackChanges() {
            this.resetEditRows();
        },
        // 汇总本地改动为后端 changes 结构
        collectChanges() {
            if (!this.canEdit) return [];
            const columns = this.result.columns || [];
            const editableSet = this.editableColumnSet;
            const pkSet = this.pkColumnSet;
            const changes = [];
            // 新增
            this.editRows.forEach(row => {
                if (!row.__new) return;
                const values = {};
                columns.forEach((col, ci) => {
                    if (!col.column) return;
                    const name = String(col.column).toLowerCase();
                    if (!editableSet.has(name) && !pkSet.has(name)) return;
                    const v = row['c' + ci];
                    if (v === null || v === undefined || String(v) === '') return; // 空则交给库默认值
                    values[col.column] = v;
                });
                if (Object.keys(values).length) changes.push({ type: 'insert', values });
            });
            // 修改（只提交被改动的可编辑列）
            this.editRows.forEach(row => {
                if (row.__new) return;
                const values = {};
                columns.forEach((col, ci) => {
                    if (!col.column) return;
                    if (!editableSet.has(String(col.column).toLowerCase())) return;
                    const now = this.normCell(row['c' + ci]);
                    const old = this.normCell(row.__orig ? row.__orig[ci] : null);
                    if (now !== old) values[col.column] = row['c' + ci];
                });
                if (Object.keys(values).length) {
                    changes.push({ type: 'update', keys: this.rowKeys(row), values });
                }
            });
            // 删除
            this.deletedRows.forEach(row => {
                changes.push({ type: 'delete', keys: this.rowKeys(row) });
            });
            return changes;
        },
        // 提交：交给父组件（生产防护锁确认 + 调接口 + 成功后重查刷新）
        async submitChanges() {
            if (!this.canEdit || !this.commitHandler) return;
            const changes = this.collectChanges();
            if (!changes.length) {
                ElMessage({ message: '没有需要提交的改动', type: 'warning', duration: 2000 });
                return;
            }
            const edit = this.editMeta || {};
            this.savingEdit = true;
            try {
                await this.commitHandler({
                    table: edit.table,
                    schema: edit.schema || '',
                    database: edit.database || '',
                    changes
                });
            } finally {
                this.savingEdit = false;
            }
        },
        // 展示用的字段类型：优先用结果自带类型；为空时回退到表字段类型
        colType(col) {
            if (col && col.type) return col.type;
            const meta = this.matchColumnMeta(col);
            return (meta && meta.type) || '';
        },
        // 字段注释：按结果列的来源表 + 原始列名去列缓存里匹配（后端已处理别名）
        colComment(col) {
            const meta = this.matchColumnMeta(col);
            return (meta && meta.comment) || '';
        },
        // 依据结果列自带的 table/column 从列缓存中取出对应字段元数据
        matchColumnMeta(col) {
            const cache = this.columnMeta;
            if (!cache || !col || !col.table || !col.column) return null;
            const key = this.resolveTableKey(col.table);
            if (!key) return null;
            const cols = cache[key] || [];
            const want = String(col.column).toLowerCase();
            return cols.find(c => c && String(c.name).toLowerCase() === want) || null;
        },
        // 列缓存的键可能是「schema.table」或裸表名，这里做「全名精确 → 短名」两步匹配
        resolveTableKey(name) {
            const cache = this.columnMeta || {};
            const want = String(name).toLowerCase();
            const keys = Object.keys(cache);
            let key = keys.find(k => k.toLowerCase() === want);
            if (key) return key;
            key = keys.find(k => k.split('.').pop().toLowerCase() === want);
            return key || '';
        },
        // 依据表头三行内容估算最小列宽：以字段名为主，类型/注释限额参与，
        // 留出排序图标与内边距的空间，避免排序按钮被挤到下一行
        colWidth(col) {
            const width = (text, unit) => {
                let units = 0;
                for (const ch of String(text || '')) {
                    units += ch.codePointAt(0) > 0x2e7f ? 2 : 1; // 中日韩/全角按两个单位
                }
                return units * unit;
            };
            const name = width(col && col.name, 7.5);
            const type = width(this.colType(col), 6.6);
            const comment = Math.min(width(this.colComment(col), 6.6), 140);
            return Math.min(320, Math.max(96, Math.ceil(Math.max(name, type, comment) + 34)));
        },
        // 展开/收起用「元素实际高度」驱动 max-height 过渡（固定大值会导致末尾突缩，即「抽一下」）
        onPanelEnter(el) {
            el.style.overflow = 'hidden';
            el.style.maxHeight = '0px';
            void el.offsetHeight; // 强制一次布局，让 0 生效
            el.style.maxHeight = el.scrollHeight + 'px';
        },
        onPanelAfterEnter(el) {
            // 动画结束清除内联高度，避免限制后续内容变化
            el.style.maxHeight = '';
            el.style.overflow = '';
        },
        onPanelLeave(el) {
            el.style.overflow = 'hidden';
            el.style.maxHeight = el.scrollHeight + 'px';
            void el.offsetHeight;
            el.style.maxHeight = '0px';
        },
        onPanelAfterLeave(el) {
            el.style.maxHeight = '';
            el.style.overflow = '';
        }
    }
};
</script>

<!-- 抽屉与弹窗会挂到 body 下，样式放非 scoped 块并统一前缀 -->
<style>
.dbresult {
    /* 执行中的加载遮罩以此为定位父级 */
    position: relative;
}

/* 执行中：在保留的旧结果上叠加半透明遮罩 + 转圈 */
.dbresult__loading {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.6);
}

.dbresult__loading .el-icon {
    color: #409eff;
}

/* 遮罩的淡入淡出 */
.dbresult-fade-enter-active,
.dbresult-fade-leave-active {
    transition: opacity 0.2s ease;
}

.dbresult-fade-enter-from,
.dbresult-fade-leave-to {
    opacity: 0;
}

.dbresult__error {
    padding: 8px 12px;
    border-radius: 8px;
    background-color: #fef0f0;
    color: #f56c6c;
    font-size: 13px;
}

.dbresult__status {
    display: flex;
    align-items: baseline;
    font-size: 13px;
    color: #5b6a80;
}

/* 本次执行的 SQL：紧跟状态行，超长省略号截断，悬浮 title 查看完整语句 */
.dbresult__sql {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #7d8ba1;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
}

.dbresult__sep {
    margin: 0 8px;
    color: #c0c4cc;
}

.dbresult__warn {
    margin-top: 8px;
}

.dbresult__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 10px 0 6px;
}

.dbresult__tips {
    font-size: 12px;
    color: #aab4c4;
}

/* 结果表格编辑：右上角操作条 */
.dbresult__editbar {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.dbresult__editbar .el-button {
    margin-left: 0;
}

.dbresult__editcount {
    font-size: 12px;
    color: #e6a23c;
}

.dbresult__editlock {
    flex: none;
    margin-left: auto;
    font-size: 12px;
    color: #aab4c4;
    cursor: help;
}

/* 可编辑单元格：默认纯文本（双击由 vxe 进入编辑），改动高亮 */
.dbresult__cell-view {
    display: block;
    min-height: 18px;
    padding: 0 4px;
    box-sizing: border-box;
    cursor: text;
}

.dbresult__cell-view.is-dirty {
    background-color: #fff8e6;
    box-shadow: 0 0 0 1px #f0c36d inset;
}

/* vxe 内置 input 渲染器（编辑态）铺满单元格 */
.dbresult__table .vxe-default-input {
    width: 100%;
    height: 100%;
    padding: 0 4px;
    box-sizing: border-box;
    border: 1px solid #409eff;
    border-radius: 3px;
    outline: none;
    font: inherit;
    color: inherit;
    background-color: #fff;
}

/* 新增行整行高亮 */
.dbresult__table .dbresult__row--new .vxe-body--column {
    background-color: #f0f9eb;
}

.dbresult__toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 8px 0 6px;
}

.dbresult__search {
    width: 260px;
}

.dbresult__match {
    font-size: 12px;
    color: #7d8ba1;
}

.dbresult__null {
    color: #aab4c4;
    font-style: italic;
}

/* 表头单元格改为 flex：标题占满剩余宽度、排序图标固定在同行右侧，
   避免自定义多行表头（块级）把排序图标挤到新的一行（浪费纵向空间） */
.dbresult__table .vxe-header--column > .vxe-cell {
    display: flex;
    align-items: center;
}

.dbresult__table .vxe-header--column > .vxe-cell > .vxe-cell--title {
    flex: 1 1 auto;
    min-width: 0;
}

.dbresult__table .vxe-header--column > .vxe-cell > .vxe-cell--sort {
    flex: 0 0 auto;
}

/* 结果列表头：三行（字段名 / 字段类型 / 字段注释），超长省略 */
.dbresult__head {
    display: flex;
    flex-direction: column;
    gap: 1px;
    overflow: hidden;
    line-height: 1.35;
}

.dbresult__head-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2d3d;
    font-weight: 600;
}

.dbresult__head-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #8a94a6;
    font-size: 11px;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
}

.dbresult__head-cmt {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #aab4c4;
    font-size: 11px;
    font-weight: 400;
}

/* 结果出现/消失：高度过渡由 JS 按「元素实际高度」驱动（见 onPanelEnter/onPanelLeave），
   这里只定义过渡曲线与淡入淡出，避免固定 max-height 造成末尾突缩（「抽一下」）。 */
.dbresult-anim-enter-active,
.dbresult-anim-leave-active {
    overflow: hidden;
    transition: max-height 0.4s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.3s ease,
        transform 0.3s ease;
}

.dbresult-anim-enter-from,
.dbresult-anim-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
